import React from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { AutoAwesomeOutlined } from '@mui/icons-material';
import { MAX_PROMPT_LENGTH } from './slidePrompts';

/**
 * The AI prompt behind each `{{slot}}` on one slide, editable next to the slide content.
 * A slot used on several slides shares one prompt, so editing it here changes it everywhere.
 *
 * @param {object} props - Props.
 * @param {number} props.slideNumber - 1-based slide number (labels only).
 * @param {string[]} props.names - Variable names on this slide, in order.
 * @param {Record<string, string>} props.prompts - Edited prompts by name.
 * @param {Record<string, string>} props.suggestions - Fallback prompt by name.
 * @param {Record<string, number[]>} props.slidesByName - Slide numbers each name appears on.
 * @param {function(string, string): void} [props.onChange] - New prompt text for a name.
 * @param {boolean} [props.readOnly] - Show the prompts as text.
 * @returns {React.ReactElement|null}
 */
function SlidePromptsPanel({ slideNumber, names, prompts, suggestions, slidesByName, onChange, readOnly = false }) {
  if (!names || names.length === 0) return null;
  const headingId = `slide-${slideNumber}-prompts`;

  return (
    <Box
      component="section"
      aria-labelledby={headingId}
      sx={{ border: 1, borderColor: 'divider', borderRadius: 'var(--q-radius-lg)', overflow: 'hidden' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderBottom: 1, borderColor: 'divider', bgcolor: 'var(--q-bg-subtle)' }}>
        <AutoAwesomeOutlined fontSize="small" sx={{ color: 'var(--q-accent)' }} aria-hidden="true" />
        <Typography id={headingId} variant="body2" fontWeight={600} component="h4" sx={{ flex: 1 }}>
          AI prompts
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {readOnly ? 'What Inky writes in each slot' : 'Tell Inky what to write in each slot. Saved with the template.'}
        </Typography>
      </Box>
      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {names.map((name) => {
          const own = Object.prototype.hasOwnProperty.call(prompts, name) ? prompts[name] : undefined;
          const suggestion = suggestions[name] || '';
          const value = own !== undefined ? own : suggestion;
          const otherSlides = (slidesByName[name] || []).filter((n) => n !== slideNumber);
          const usesFallback = own !== undefined && !own.trim();
          return (
            <Box component="li" key={name} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 1 }}>
                <Box
                  component="code"
                  sx={{
                    fontFamily: 'var(--q-font-mono)',
                    fontSize: 12.5,
                    px: 0.5,
                    borderRadius: '4px',
                    bgcolor: 'var(--q-warning-soft)',
                    color: 'var(--q-warning-text)',
                  }}
                >
                  {`{{${name}}}`}
                </Box>
                {otherSlides.length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Shared with slide{otherSlides.length === 1 ? '' : 's'} {otherSlides.join(', ')}
                  </Typography>
                )}
              </Box>
              {readOnly ? (
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                  {value || 'No prompt yet.'}
                </Typography>
              ) : (
                <TextField
                  value={value}
                  onChange={(e) => onChange?.(name, e.target.value)}
                  placeholder="Describe what Inky should write here"
                  helperText={usesFallback && suggestion ? `Empty prompts use the suggestion: ${suggestion}` : undefined}
                  size="small"
                  fullWidth
                  multiline
                  minRows={1}
                  maxRows={6}
                  inputProps={{ maxLength: MAX_PROMPT_LENGTH, 'aria-label': `AI prompt for ${name}` }}
                />
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default SlidePromptsPanel;
